import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { LOCALE_TYPE } from "src/constants";
import {
  getSelectedLocale,
  markSessionIsReloading,
  markSessionReuseUnblocked,
  setSelectedLocale,
} from "src/utils/storage-utils";
import { useDispatch } from "react-redux";
import { searchDataActions } from "src/store/search";
import { useTenantContext } from "./tenant-context";
import { useLoaderData, useLocation, useNavigate } from "react-router";

const CURRENCY_SYMBOLS = {
  EUR: "€",
  USD: "$",
  PLN: "zł",
  GBP: "£",
  RUB: "₽",
};

const LANG_VALUES = {
  [LOCALE_TYPE.GB]: ["English", "Latvian", "Russian", "Lithuanian"],
  [LOCALE_TYPE.LV]: ["Angļu", "Latviešu", "Krievu", "Lietuviešu"],
  [LOCALE_TYPE.LT]: ["Anglų", "Latvių", "Rusų", "Lietuvių"],
  [LOCALE_TYPE.RU]: ["Английский", "Латышский", "Русский", "Литовский"],
};

const LocaleContext = createContext({
  currentLocale: "",
  currentLanguage: "",
  currency: "",
  currencySymbol: "",
  defaultLang: "",
  getStringRes: () => {},
  setSiteLanguage: () => {},
  languages: [],
  currencies: [],
});

const LANG_MAP = { en: "en_GB", lv: "lv_LV", ru: "ru_RU", lt: "lt_LT" };

function getLanguageData(locale, allowedLangs) {
  const labels = LANG_VALUES[locale];
  return [
    { value: LOCALE_TYPE.GB, label: labels[0] },
    { value: LOCALE_TYPE.LV, label: labels[1] },
    { value: LOCALE_TYPE.RU, label: labels[2] },
    { value: LOCALE_TYPE.LT, label: labels[3] },
  ].filter((l) => allowedLangs.includes(l.value));
}

export const LocaleContextManager = (props) => {
  const { strings, lang } = useLoaderData();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currency, languages: siteLanguages } = useTenantContext();
  const [state, setState] = useState({
    defaultLang: window.SERVER_DATA.REACT_APP_DEFAULT_DOMAIN_LOCALE,
    currentLocale: LANG_MAP[lang],
    currentLanguage: lang,
    currency: currency || "EUR",
    currencySymbol: CURRENCY_SYMBOLS.EUR,
    stringRes: strings || {},
    languages: getLanguageData(LANG_MAP[lang], siteLanguages),
    currencies: [{ value: "eur", label: "Euro - EUR" }],
  });

  const getStringRes = useCallback((key) => state.stringRes[key] || key, [state]);

  useEffect(() => {
    const loc = LANG_MAP[lang];
    setState((prev) => {
      return {
        ...prev,
        currentLocale: LANG_MAP[lang],
        currentLanguage: lang,
        stringRes: strings,
        languages: getLanguageData(loc, siteLanguages),
      };
    });
    setSelectedLocale(loc);
    dispatch(searchDataActions.changeCityDisplayLanguage(lang.toUpperCase()));
  }, [dispatch, lang, siteLanguages, strings]);

  const onChangeSiteLanguage = useCallback(
    (lang) => {
      let sq = new URLSearchParams(location.search);
      sq.set("lang", lang);
      markSessionReuseUnblocked(); // this will allow booking to be reloaded without restarting booking
      markSessionIsReloading(); // this will allow booking to be reloaded without restarting booking
      if (LANG_MAP[lang]) {
        setSelectedLocale(LANG_MAP[lang]);
        document.documentElement.lang = lang;
      }
      navigate(`${location.pathname}?${sq.toString()}${location.hash || ""}`, {
        state: location.state,
        replace: true,
      });
    },
    [location.search, location.pathname, location.hash, location.state, navigate],
  );

  useEffect(() => {
    let currentLang = lang;
    console.log("hook lang is", lang);
    if (!lang || !LANG_MAP[lang] || !siteLanguages.includes(LANG_MAP[lang])) {
      let defaultLocale = getSelectedLocale();
      if (!siteLanguages.includes(defaultLocale)) {
        defaultLocale = siteLanguages[0];
        setSelectedLocale(defaultLocale);
      }
      currentLang = defaultLocale.split("_")[0];
    }
    if (document.documentElement.lang !== currentLang) {
      document.documentElement.lang = currentLang;
    }
    if (lang !== currentLang) {
      onChangeSiteLanguage(currentLang);
    }
  }, [lang, onChangeSiteLanguage, siteLanguages]);

  return (
    <LocaleContext.Provider
      value={{
        ...state,
        getStringRes,
        setSiteLanguage: onChangeSiteLanguage,
      }}>
      {state.currentLocale && props.children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;

export const useLocaleContext = () => useContext(LocaleContext);
